import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
	PiArrowLeftLight,
	PiClock,
	PiEye,
	PiFilePdfLight,
	PiMagnifyingGlassMinus,
	PiMagnifyingGlassPlus,
} from "react-icons/pi";
import LoadingIcons from "react-loading-icons";
import { getDocument } from "pdfjs-dist";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry"; // Required: loads the PDF worker
import { GlobalContext } from "../contexts/GlobalContext";
import client from "../client";
import { ContentContainer } from "coaz-ui";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

const FileResource = () => {
	const { fileId } = useParams();
	const { version, setVersion } = useContext(GlobalContext);
	const path = useLocation().pathname;
	const [iframeSrc, setIframeSrc] = useState(null);
	const [fileName, setFileName] = useState(null);
	const [viewed, setViewed] = useState(0);
	const [duration, setDuration] = useState(0);
	const [current,setCurrent] = useState(null);
	const [trackViewed, setTrackViewed] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const containerRef = useRef(null);
	const documentRef = useRef(null);
	const pageRef = useRef(null);
	const viewedRef = useRef(viewed);
	const maxPageWidth = 640;
	const pagePadding = 20;
	const [pageWidth, setPageWidth] = useState(maxPageWidth);
	const [scale, setScale] = useState(1); // Default zoom level

	const { request, getCurrent, download, errorMessage } = client;

	const navigate = useNavigate();

	useEffect(() => {
		viewedRef.current = viewed;
	}, [viewed]);

	useEffect(() => {
		(async () => {
			setLoading(true);

			try {
				const curr = await getCurrent();

				if (!curr || !curr.user) return;

				setCurrent(curr);

				const file = await download(`file-resources/file/${fileId}`);

				if (file && file.data instanceof Blob) {
					const url = URL.createObjectURL(
						new Blob([file.data], {
							type: file.data?.type ?? "application/pdf",
						}),
					);
					setIframeSrc(url);

					const fileResource = await request(
						"GET",
						`file-resources/${fileId}`,
					);

					if (!fileResource || !fileResource.teacherReference) return;

					setFileName(fileResource.name);
					setDuration(fileResource.durationInMilliSeconds);

					if (
						curr.user.ref.value === fileResource.teacherReference
					) {
						setTrackViewed(false);
						return;
					}

					setTrackViewed(true);

					const data = {
						studentReference: curr.user.ref.value,
						resourceId: fileResource.ref.id,
					};

					let completion;

					try {
						completion = await request(
							"GET",
							"file-resource-completions/by-id",
							null,
							data,
						);
					} catch {
						try {
							completion = await request(
								"POST",
								"file-resource-completions",
								null,
								data,
							);
						} catch {}
					}

					setViewed(completion?.viewed ?? 0);
				}
			} catch (error) {
				console.log(error);
				setError(errorMessage(error));
			} finally {
				setLoading(false);
			}
		})();

		const observer = new ResizeObserver((entries) => {
			for (let entry of entries) {
				if (documentRef.current) {
					let documentWidth =
						documentRef.current.getBoundingClientRect().width;
					if (documentWidth > maxPageWidth + pagePadding * 2) {
						setPageWidth(maxPageWidth);
					} else {
						let width = documentWidth - pagePadding * 2;
						setPageWidth(width);
					}
				}
			}
		});

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => {
			observer.disconnect();
			if (viewedRef.current) {
				//request('POST','resource/view',null,{fileId:fileId,viewed:viewedRef.current},true);
			}
		};
	}, [fileId]);

	return (
		<ContentContainer
			Icon={PiFilePdfLight}
			text={fileName ? fileName : ""}
			loading={loading}
		>
			{error ? (
				<div className="flex w-full h-full items-center justify-center">
					<p className="text-red-400 italic">{error}</p>
				</div>
			) : (
				<div
					ref={containerRef}
					className="relative flex flex-col w-full h-full"
				>
					<div className="absolute flex flex-row left-1/2 -translate-x-1/2 bottom-4 w-fit h-10 px-2 py-1 space-x-4 items-center justify-center bg-white shadow-lg rounded-full z-50">
						{trackViewed && (
							<>
								<div className="flex flex-row space-x-1 w-fit h-fit items-center text-sm text-[rgb(68,71,70)] font-helveticaNeueRegular">
									<PiEye size={16} />
									<Timer
										timer={viewed}
										setTimer={setViewed}
										minMs={duration}
										studentReference={current.user.ref.value}
										resourceId={fileId}
									/>
									{/* <Timer
										timer={viewed}
										setTimer={setViewed}
										minMs={duration}
									/> */}
								</div>
								<div className="flex flex-row space-x-1 w-fit h-fit items-center text-sm text-[rgb(68,71,70)] font-helveticaNeueRegular">
									<PiClock size={16} />
									<FormatedTime time={duration} />
								</div>
							</>
						)}
						<div className="flex flex-row space-x-1 w-fit h-fit items-center text-xs text-[rgb(68,71,70)] font-helveticaNeueRegular">
							<button
								onClick={() =>
									setScale((s) => Math.max(0.25, s - 0.25))
								}
							>
								<PiMagnifyingGlassMinus size={16} />
							</button>
							<span style={{ margin: "0 10px" }}>
								{scale.toFixed(2)}x
							</span>
							<button
								onClick={() =>
									setScale((s) => Math.min(5, s + 0.25))
								}
							>
								<PiMagnifyingGlassPlus size={16} />
							</button>
						</div>
					</div>
					<div className="flex w-full h-full overflow-auto [scrollbar-color:#888_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400">
						<PdfViewer url={iframeSrc} scale={scale} />
					</div>
				</div>
			)}
		</ContentContainer>
	);
};

export default FileResource;

const Timer = ({ timer, setTimer, minMs, studentReference, resourceId }) => {
    const lastUpdate = useRef(Date.now());

	const {request} = client;

    useEffect(() => {
        lastUpdate.current = Date.now();

        const saveViewed = (elapsedMilliseconds) => {
            if (elapsedMilliseconds <= 0) return;

            request('PUT', 'file-resource-completions/viewing-time', null, {
                studentReference,
                resourceId,
                updateId: crypto.randomUUID(),
                elapsedMilliseconds
            }).catch(console.error);
        };

        const intervalId = setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastUpdate.current;

            lastUpdate.current = now;
            setTimer(previous => previous + elapsed);
            saveViewed(elapsed);
        }, 1000);

        return () => {
            clearInterval(intervalId);

            const elapsed = Date.now() - lastUpdate.current;
            saveViewed(elapsed);
        };
    }, [studentReference, resourceId, setTimer]);

    return <FormatedTime time={timer} minimum={minMs} />;
};

/* const Timer = ({ timer, setTimer, minMs }) => {
	const start = new Date();

	useEffect(() => {
		const timerId = setTimeout(() => {
			const elapsed = new Date().getTime() - start.getTime();
			setTimer(timer + elapsed);
		}, 1000);
		return () => {
			clearTimeout(timerId);
		};
	}, [timer]);

	return <FormatedTime time={timer} minimum={minMs} />;
}; */

const FormatedTime = ({ time, minimum }) => {
	let totalSeconds = parseInt(Math.floor(time / 1000));
	let totalMinutes = parseInt(Math.floor(totalSeconds / 60));
	let totalHours = parseInt(Math.floor(totalMinutes / 60));
	let days = parseInt(totalHours / 24);

	let seconds = parseInt(totalSeconds % 60);
	let minutes = parseInt(totalMinutes % 60);
	let hours = parseInt(totalHours % 24);

	return (
		<p
			className={`text-xs ${minimum && (minimum === 0 || time > minimum) ? "text-green-600" : "text-[rgb(68,70,71)]"}`}
		>
			{`${days}:${hours}:${minutes}:${seconds}`}
		</p>
	);
};

function PdfViewer({ url, scale }) {
	const parentRef = useRef();
	const [pdf, setPdf] = useState(null);
	const [pageHeights, setPageHeights] = useState([]);
	const [pageCount, setPageCount] = useState(0);

	useEffect(() => {
		const loadPdf = async () => {
			if (!url) return;
			const loadingTask = getDocument(url);
			const pdfDoc = await loadingTask.promise;
			setPdf(pdfDoc);
			setPageCount(pdfDoc.numPages);
		};
		loadPdf();
	}, [url]);

	// Update page heights when scale or pdf changes
	useEffect(() => {
		if (!pdf) return;

		const updateHeights = async () => {
			const heights = [];

			for (let i = 1; i <= pdf.numPages; i++) {
				const page = await pdf.getPage(i);
				const viewport = page.getViewport({ scale });
				heights.push(viewport.height);
			}
			setPageHeights(heights);
		};

		updateHeights();
	}, [pdf, scale]);

	if (!pdf || pageHeights.length === 0) return <div>Loading PDF...</div>;

	return (
		<VirtualPdfPages
			pdf={pdf}
			pageHeights={pageHeights}
			parentRef={parentRef}
			pageCount={pageCount}
			scale={scale}
		/>
	);
}

const VirtualPdfPages = ({ pdf, pageHeights, parentRef, pageCount, scale }) => {
	const virtualizer = useVirtualizer({
		count: pageCount,
		getScrollElement: () => parentRef.current,
		estimateSize: (i) => pageHeights[i],
		overscan: 2,
	});
	const [totalHeight, setTotalHeight] = useState(virtualizer.getTotalSize());

	useEffect(() => {
		virtualizer.measure(); // Recomputes total size and offsets
		setTotalHeight(virtualizer.getTotalSize());
	}, [pageHeights, virtualizer]);

	return (
		<div ref={parentRef} className="relative w-full h-full">
			<div
				style={{
					height: totalHeight,
					position: "relative",
				}}
			>
				{virtualizer.getVirtualItems().map((virtualRow) => (
					<div
						key={virtualRow.key}
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							transform: `translateY(${virtualRow.start}px)`,
							width: "100%",
						}}
					>
						<PdfPageRenderer
							pdf={pdf}
							pageNumber={virtualRow.index + 1}
							scale={scale}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

const PdfPageRenderer = ({ pdf, pageNumber, scale }) => {
	const canvasRef = useRef();

	useEffect(() => {
		const renderPage = async () => {
			if (!pdf) return;
			const page = await pdf.getPage(pageNumber);
			const viewport = page.getViewport({ scale });
			const canvas = canvasRef.current;
			const context = canvas.getContext("2d");
			canvas.height = viewport.height;
			canvas.width = viewport.width;
			// Cancel previous render if running
			if (canvas._renderTask) {
				canvas._renderTask.cancel();
			}

			const renderContext = {
				canvasContext: context,
				viewport,
			};

			const renderTask = page.render(renderContext);
			canvas._renderTask = renderTask;

			try {
				await renderTask.promise;
			} catch (e) {
				/* if (e?.name === 'RenderingCancelledException') {
			console.log(`Render of page ${pageNumber} cancelled`);
			} else {
			console.error('Render error', e);
			} */
			}
		};

		renderPage();
	}, [pdf, pageNumber, scale]);

	return (
		<canvas
			className="border border-gray-200"
			ref={canvasRef}
			style={{ display: "block", margin: "0 auto" }}
		/>
	);
};
